import { EncryptJWT, importJWK } from "jose";
import { useEffect, useState } from "react";

import { getBootstrapData } from "@swa-ui/bootstrap";
import { swaDate } from "@swa-ui/date";
import { isResponseOk, useFetch } from "@swa-ui/fetch";
import { usePersistedState } from "@swa-ui/persistence";

import { ENCRYPTION_TYPE } from "../defines/constants";
import { STATE_SETTINGS } from "../defines/stateSettings";
import { getPaymentCardEncryptionSchema } from "./usePaymentCardEncryption.schema";

/**
 * Hook for encrypting payment card numbers using JWKS keys with server-time synchronized expiration.
 * This hook handles fetching encryption keys, synchronizing with server time to ensure accurate
 * key expiration, and encrypting card data using JWE.
 *
 * @param {string} jwtConfigKey - JWT configuration key
 * @param {boolean} [fetchKeysOnMount=false] - Whether to fetch keys on mount
 * @returns {function(string[]): Promise<string|null>} returns.getEncryptedToken - Encrypts data using cached keys or fetches new ones if needed
 * @returns {function(string[]): Promise<string|null>} returns.refreshKeysAndGetEncryptedToken - Fetches fresh keys, and encrypts
 * @returns {Function} returns.retrieveJwksKeys - Fetches and caches new JWKS keys
 * @returns {boolean} returns.error - Indicates if an error occurred during encryption process
 * @returns {string} returns.errorMessage - Error message if an error occurred
 * @returns {boolean} returns.loading - Indicates if a key retrieval operation is in progress
 */
export const usePaymentCardEncryption = (jwtConfigKey, fetchKeysOnMount = false) => {
  const [jwksPublicKey, setJwksPublicKey] = usePersistedState(
    STATE_SETTINGS.paymentEncryptionJwksKey
  );
  const [errorMessage, setErrorMessage] = useState("");
  const { configs: jwtConfigs, keyConfigMap } = getBootstrapData("jwt-settings") ?? {};
  const jwtConfig = jwtConfigs?.find((config) => config.id === keyConfigMap?.[jwtConfigKey]) ?? {};
  const { enc, endpointUrl } = jwtConfig;
  const { get, loading, response } = useFetch(endpointUrl);
  const paymentCardEncyptionSchema = getPaymentCardEncryptionSchema();

  useEffect(() => {
    if (fetchKeysOnMount && !isValidJwksKey(jwksPublicKey)) {
      retrieveJwksKeys();
    }
  }, [fetchKeysOnMount, jwksPublicKey]);

  async function retrieveJwksKeys() {
    let result = null;

    if (!loading) {
      try {
        await get();

        if (isResponseOk(response)) {
          const {
            headers,
            data: { keys },
          } = response;

          if (keys?.length) {
            const encryptionKey = keys.find((key) => key.use === "enc");
            const { expiryTime, timeDifference } = calculateExpiryTime(headers);
            const keyWithExpiry = {
              ...encryptionKey,
              expires_on: expiryTime,
              time_difference: timeDifference,
            };

            setJwksPublicKey(keyWithExpiry);
            setErrorMessage("");
            result = keyWithExpiry;
          } else {
            throw new Error("No valid keys found in JWKS response");
          }
        } else {
          throw new Error("Failed to retrieve JWKS keys");
        }
      } catch (err) {
        setErrorMessage(err?.message);
      }
    }

    return result;
  }

  async function encryptWithKey(cardNumbers, jwk) {
    let encryptedToken = null;

    try {
      const payloadForJWE = paymentCardEncyptionSchema.validateSync(getJWEPayload(cardNumbers));
      const { alg, e, kid, kty, n, use, x5t } = jwk;
      const base64UrlEncodedValue = convertBase64ToBase64Url(n);
      const rsaPublicKey = await importJWK(
        { e, kid, kty, n: base64UrlEncodedValue, use, x5t },
        alg
      );

      encryptedToken = await new EncryptJWT(payloadForJWE)
        .setProtectedHeader({ alg, enc, kid, typ: ENCRYPTION_TYPE })
        .encrypt(rsaPublicKey);
    } catch (err) {
      setErrorMessage(err || "Failed to encrypt data");
    }

    return encryptedToken;
  }

  async function getEncryptedToken(cardNumbers) {
    let jwe = null;

    try {
      if (cardNumbers?.length) {
        let jwk = jwksPublicKey;

        if (!isValidJwksKey(jwk)) {
          jwk = await retrieveJwksKeys();
        }

        if (jwk) {
          jwe = await encryptWithKey(cardNumbers, jwk);
        }
      } else {
        throw new Error("Invalid card numbers format");
      }
    } catch (err) {
      setErrorMessage(err?.message || "Failed to generate token");
    }

    return jwe;
  }

  async function refreshKeysAndGetEncryptedToken(cardNumbers) {
    let jwe = null;

    try {
      if (cardNumbers?.length) {
        const jwk = await retrieveJwksKeys();

        if (jwk) {
          jwe = await encryptWithKey(cardNumbers, jwk);
        }
      } else {
        throw new Error("Invalid card numbers format");
      }
    } catch (err) {
      setErrorMessage(err?.message || "Failed to generate token");
    }

    return jwe;
  }

  function getJWEPayload(cardNumbers) {
    const formattedCardNumbers = cardNumbers?.reduce((accumulator, cardNumber, index) => {
      accumulator[`cardNumber${index + 1}`] = cardNumber;

      return accumulator;
    }, {});

    return { creditCardNumbers: formattedCardNumbers };
  }

  function isValidJwksKey(jwk = {}) {
    const { alg, e, kid, n, use, expires_on, time_difference = 0 } = jwk;
    const hasRequiredFields = Boolean(alg && e && kid && n && use);
    const adjustedNow = swaDate().valueOf() - time_difference;

    return hasRequiredFields && use === "enc" && expires_on && adjustedNow < expires_on;
  }

  function convertBase64ToBase64Url(base64String) {
    return base64String?.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  }

  /**
   * Calculates the expiry time for a key based on server time and cache headers.
   * Uses server time reference to ensure accurate expiration regardless of client clock.
   *
   * @param {Headers} headers - Response headers
   * @returns {Object} Object containing expiryTime and timeDifference
   */
  function calculateExpiryTime(headers) {
    const cacheControl = headers?.get("Cache-Control");
    const serverDate = headers?.get("Date");
    let timeDifference = 0;
    let maxAge = 3600000;

    if (serverDate) {
      timeDifference = swaDate().diff(swaDate(serverDate));
    }

    if (cacheControl) {
      const maxAgeMatch = cacheControl.match(/max-age=(\d+)/);

      if (maxAgeMatch && maxAgeMatch[1]) {
        maxAge = parseInt(maxAgeMatch[1], 10) * 1000;
      }
    }

    const expiryTime = swaDate().add(maxAge, "millisecond").valueOf();

    return { expiryTime, timeDifference };
  }

  return {
    error: Boolean(errorMessage),
    errorMessage,
    getEncryptedToken,
    loading: loading,
    refreshKeysAndGetEncryptedToken,
    retrieveJwksKeys,
  };
};
